const SKELETON_STYLE_ID = 'peachpay-checkout-skeleton-styles';
const SKELETON_ID = 'peachpay-checkout-skeleton';

const skeletonStyles = `
	#${SKELETON_ID} {
		position: fixed;
		inset: 0;
		z-index: 2147483647;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 20px;
		background: transparent;
		font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
		-webkit-font-smoothing: antialiased;
	}

	#${SKELETON_ID}, #${SKELETON_ID} * { box-sizing: border-box; }

	#${SKELETON_ID} .pp-checkout-skeleton__modal {
		display: flex;
		width: min(94vw, 780px);
		max-height: 92vh;
		overflow: hidden;
		background: #fff;
		border: 3px solid #e8a838;
		border-radius: 10px;
		box-shadow: 0 24px 70px rgb(0 0 0 / 28%);
	}

	#${SKELETON_ID} .pp-checkout-skeleton__left { flex: 1; padding: 24px 28px 20px; }
	#${SKELETON_ID} .pp-checkout-skeleton__tabs,
	#${SKELETON_ID} .pp-checkout-skeleton__row,
	#${SKELETON_ID} .pp-checkout-skeleton__product,
	#${SKELETON_ID} .pp-checkout-skeleton__summary-row { display: flex; }
	#${SKELETON_ID} .pp-checkout-skeleton__tabs { margin-bottom: 24px; }
	#${SKELETON_ID} .pp-checkout-skeleton__tab { flex: 1; }
	#${SKELETON_ID} .pp-checkout-skeleton__tab-label {
		margin-bottom: 6px;
		color: #bbb;
		font-size: 13px;
		font-weight: 700;
		letter-spacing: 0.5px;
		text-transform: uppercase;
	}

	#${SKELETON_ID} .pp-checkout-skeleton__tab:first-child .pp-checkout-skeleton__tab-label { color: #1a1a3e; }
	#${SKELETON_ID} .pp-checkout-skeleton__tab-bar { height: 4px; background: #e5e5e5; border-radius: 2px; }
	#${SKELETON_ID} .pp-checkout-skeleton__tab:first-child .pp-checkout-skeleton__tab-bar { background: #1a1a3e; }
	#${SKELETON_ID} .pp-checkout-skeleton__row { gap: 12px; }

	#${SKELETON_ID} .pp-checkout-skeleton__field {
		display: flex;
		flex: 1;
		flex-direction: column;
		justify-content: center;
		height: 54px;
		margin-bottom: 12px;
		padding: 8px 12px;
		border: 1px solid #ddd;
		border-radius: 6px;
	}

	#${SKELETON_ID} .pp-checkout-skeleton__field--wide { flex: 1.3; }
	#${SKELETON_ID} .pp-checkout-skeleton__field--narrow { flex: 0.7; }
	#${SKELETON_ID} .pp-checkout-skeleton__label { width: 65px; height: 8px; margin-bottom: 6px; }
	#${SKELETON_ID} .pp-checkout-skeleton__value { height: 14px; }
	#${SKELETON_ID} .pp-checkout-skeleton__button {
		height: 48px;
		margin-top: 4px;
		background: #1a1a3e;
		border-radius: 8px;
		opacity: 0.12;
	}

	#${SKELETON_ID} .pp-checkout-skeleton__exit { width: 80px; height: 11px; margin: 12px auto 0; }
	#${SKELETON_ID} .pp-checkout-skeleton__right {
		position: relative;
		flex-shrink: 0;
		width: 280px;
		padding: 24px;
		border-left: 1px solid #eee;
	}

	#${SKELETON_ID} .pp-checkout-skeleton__close {
		position: absolute;
		top: 12px;
		right: 16px;
		padding: 4px;
		color: #999;
		font-size: 22px;
		line-height: 1;
		background: transparent;
		border: 0;
		cursor: pointer;
	}

	#${SKELETON_ID} .pp-checkout-skeleton__title { margin-bottom: 18px; color: #222; font-size: 16px; font-weight: 700; }
	#${SKELETON_ID} .pp-checkout-skeleton__product { gap: 12px; margin-bottom: 14px; }
	#${SKELETON_ID} .pp-checkout-skeleton__product-image { flex-shrink: 0; width: 64px; height: 64px; border-radius: 6px; }
	#${SKELETON_ID} .pp-checkout-skeleton__product-info { display: flex; flex: 1; flex-direction: column; gap: 5px; }
	#${SKELETON_ID} .pp-checkout-skeleton__line { height: 10px; }
	#${SKELETON_ID} .pp-checkout-skeleton__product-meta { display: flex; flex-shrink: 0; flex-direction: column; align-items: flex-end; gap: 8px; }
	#${SKELETON_ID} .pp-checkout-skeleton__price { width: 60px; height: 12px; }
	#${SKELETON_ID} .pp-checkout-skeleton__remove { width: 16px; height: 16px; }
	#${SKELETON_ID} .pp-checkout-skeleton__divider { height: 1px; margin: 14px 0; background: #eee; }
	#${SKELETON_ID} .pp-checkout-skeleton__summary-row { justify-content: space-between; margin-bottom: 8px; }
	#${SKELETON_ID} .pp-checkout-skeleton__summary-label,
	#${SKELETON_ID} .pp-checkout-skeleton__summary-value { height: 11px; }

	#${SKELETON_ID} .pp-checkout-skeleton__shimmer {
		background: linear-gradient(90deg, #f0f0f0 25%, #fafafa 50%, #f0f0f0 75%);
		background-size: 200% 100%;
		border-radius: 3px;
		animation: peachpay-checkout-skeleton-shimmer 1.6s ease-in-out infinite;
	}

	@keyframes peachpay-checkout-skeleton-shimmer {
		0% { background-position: 200% 0; }
		100% { background-position: -200% 0; }
	}

	@media (max-width: 640px) {
		#${SKELETON_ID} .pp-checkout-skeleton__modal { flex-direction: column; width: min(96vw, 420px); }
		#${SKELETON_ID} .pp-checkout-skeleton__right { width: auto; border-top: 1px solid #eee; border-left: 0; }
	}
`;

type CheckoutSkeletonOptions = {
	onCancel?: () => void;
};

let skeletonEscapeKeyHandler: ((event: KeyboardEvent) => void) | null = null;

function showCheckoutSkeleton(options: CheckoutSkeletonOptions = {}): void {
	injectSkeletonStyles();
	hideCheckoutSkeleton();

	const cancelCheckout = (event: Event) => {
		event.preventDefault();
		options.onCancel?.();
	};

	const $skeleton = document.createElement('div');
	$skeleton.id = SKELETON_ID;
	$skeleton.setAttribute('role', 'status');
	$skeleton.setAttribute('aria-live', 'polite');
	$skeleton.setAttribute('aria-busy', 'true');
	$skeleton.setAttribute('aria-label', 'Loading PeachPay checkout');
	$skeleton.append(createSkeletonModal(cancelCheckout));
	$skeleton.addEventListener('click', event => {
		if (event.target === $skeleton) {
			cancelCheckout(event);
		}
	});

	skeletonEscapeKeyHandler = event => {
		if (event.key === 'Escape') {
			cancelCheckout(event);
		}
	};

	document.addEventListener('keydown', skeletonEscapeKeyHandler);
	document.body.append($skeleton);
}

function hideCheckoutSkeleton(): void {
	if (skeletonEscapeKeyHandler) {
		document.removeEventListener('keydown', skeletonEscapeKeyHandler);
		skeletonEscapeKeyHandler = null;
	}

	document.querySelector(`#${SKELETON_ID}`)?.remove();
}

function injectSkeletonStyles(): void {
	if (document.querySelector(`#${SKELETON_STYLE_ID}`)) {
		return;
	}

	const $style = document.createElement('style');
	$style.id = SKELETON_STYLE_ID;
	$style.textContent = skeletonStyles;
	document.head.append($style);
}

function createSkeletonModal(cancelCheckout: (event: Event) => void): HTMLElement {
	const $modal = createElement('div', 'pp-checkout-skeleton__modal');
	const $left = createElement('div', 'pp-checkout-skeleton__left');
	const $right = createElement('div', 'pp-checkout-skeleton__right');

	$left.append(createTabs(), ...createFieldRows(), createElement('div', 'pp-checkout-skeleton__button'), createElement('div', 'pp-checkout-skeleton__exit pp-checkout-skeleton__shimmer'));
	$right.append(createCloseButton(cancelCheckout), createTextElement('div', 'pp-checkout-skeleton__title', 'Order summary'), createProductSummary(), createElement('div', 'pp-checkout-skeleton__divider'), createSummaryRow('55px', '65px'), createSummaryRow('50px', '40px'), createElement('div', 'pp-checkout-skeleton__divider'), createSummaryRow('40px', '70px'), createElement('div', 'pp-checkout-skeleton__divider'), createLine('130px'));
	$modal.append($left, $right);

	return $modal;
}

function createCloseButton(cancelCheckout: (event: Event) => void): HTMLButtonElement {
	const $button = createTextElement('button', 'pp-checkout-skeleton__close', '×');
	$button.type = 'button';
	$button.setAttribute('aria-label', 'Close checkout');
	$button.addEventListener('click', event => {
		event.stopPropagation();
		cancelCheckout(event);
	});

	return $button;
}

function createTabs(): HTMLElement {
	const $tabs = createElement('div', 'pp-checkout-skeleton__tabs');
	$tabs.append(...['Billing', 'Shipping', 'Payment'].map(createTab));

	return $tabs;
}

function createTab(label: string): HTMLElement {
	const $tab = createElement('div', 'pp-checkout-skeleton__tab');
	$tab.append(createTextElement('div', 'pp-checkout-skeleton__tab-label', label), createElement('div', 'pp-checkout-skeleton__tab-bar'));

	return $tab;
}

function createFieldRows(): HTMLElement[] {
	const rows = [
		[createField('', '80%'), createField('', '60%')],
		[createField('', '50%'), createField('', '60%')],
		[createField('pp-checkout-skeleton__field--wide', '70%'), createField('pp-checkout-skeleton__field--narrow', '40%')],
		[createField('', '50%'), createField('', '60%')],
		[createField('', '40%'), createField('', '70%')],
	];

	return rows.map(row => {
		const $row = createElement('div', 'pp-checkout-skeleton__row');
		$row.append(...row);

		return $row;
	});
}

function createField(extraClass: string, valueWidth: string): HTMLElement {
	const className = ['pp-checkout-skeleton__field', extraClass].filter(Boolean).join(' ');
	const $field = createElement('div', className);
	$field.append(createElement('div', 'pp-checkout-skeleton__label pp-checkout-skeleton__shimmer'), createLineElement('pp-checkout-skeleton__value pp-checkout-skeleton__shimmer', valueWidth));

	return $field;
}

function createProductSummary(): HTMLElement {
	const $product = createElement('div', 'pp-checkout-skeleton__product');
	const $productInfo = createElement('div', 'pp-checkout-skeleton__product-info');
	const $productMeta = createElement('div', 'pp-checkout-skeleton__product-meta');
	$productInfo.append(...['95%', '80%', '55%', '65%'].map(createLine));
	$productMeta.append(createElement('div', 'pp-checkout-skeleton__price pp-checkout-skeleton__shimmer'), createElement('div', 'pp-checkout-skeleton__remove pp-checkout-skeleton__shimmer'));
	$product.append(createElement('div', 'pp-checkout-skeleton__product-image pp-checkout-skeleton__shimmer'), $productInfo, $productMeta);

	return $product;
}

function createLine(width: string): HTMLElement {
	return createLineElement('pp-checkout-skeleton__line pp-checkout-skeleton__shimmer', width);
}

function createSummaryRow(labelWidth: string, valueWidth: string): HTMLElement {
	const $row = createElement('div', 'pp-checkout-skeleton__summary-row');
	$row.append(createLineElement('pp-checkout-skeleton__summary-label pp-checkout-skeleton__shimmer', labelWidth), createLineElement('pp-checkout-skeleton__summary-value pp-checkout-skeleton__shimmer', valueWidth));

	return $row;
}

function createLineElement(className: string, width: string): HTMLElement {
	const $element = createElement('div', className);
	$element.style.width = width;

	return $element;
}

function createTextElement<T extends keyof HTMLElementTagNameMap>(tagName: T, className: string, text: string): HTMLElementTagNameMap[T] {
	const $element = createElement(tagName, className);
	$element.textContent = text;

	return $element;
}

function createElement<T extends keyof HTMLElementTagNameMap>(tagName: T, className: string): HTMLElementTagNameMap[T] {
	const $element = document.createElement(tagName);
	$element.className = className;

	return $element;
}

export {hideCheckoutSkeleton, showCheckoutSkeleton};
